import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, ShieldCheck, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import client from "@/lib/api";
import { SeoContent } from "@/components/landing/SeoContent";

const HERO_COLLAGE =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_wedding-pricing-hub/artifacts/hs1qcup1_WVE%20DESKTOP%20BACKGROUND.jpg";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_wedding-pricing-hub/artifacts/0qbdzfob_WVE%20LOGO.png";

export default function Landing() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const retrieveRef = useRef(null);

  async function onRetrieve(e) {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await client.post("/public/quotes/by-email", { email });
      setSent(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function scrollToRetrieve() {
    retrieveRef.current?.scrollIntoView({ behavior: "smooth" });
  }

  return (
    <div className="min-h-screen w-full bg-[#FAF9F6]" data-testid="landing-page">
      {/* Hero */}
      <section className="relative h-screen min-h-[640px] w-full overflow-hidden">
        <img
          src={HERO_COLLAGE}
          alt=""
          className="absolute inset-0 w-full h-full object-cover blur-[4px] scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/85" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_30%,_rgba(0,0,0,0.45)_100%)]" />

        {/* Top bar */}
        <header className="absolute top-0 inset-x-0 z-20 flex items-center justify-between px-6 md:px-12 lg:px-24 py-6">
          <Link to="/" className="inline-flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="Wedding Video Essex"
              className="h-16 md:h-20 w-auto object-contain brightness-0 invert opacity-95"
              data-testid="brand-logo"
            />
          </Link>
          <Link
            to="/admin/login"
            data-testid="admin-login-link"
            className="group inline-flex items-center gap-2 text-white/80 hover:text-white text-xs tracking-overline uppercase font-medium border border-white/30 hover:border-white/70 px-5 py-3 transition-all duration-300 backdrop-blur-sm"
          >
            <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
            Admin
          </Link>
        </header>

        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 fade-up">
          <div className="text-[#CCBA56] text-xs md:text-sm tracking-[0.25em] uppercase font-medium mb-6">
            Wedding Videographer &amp; Photographer Essex
          </div>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-medium max-w-4xl drop-shadow-[0_4px_20px_rgba(0,0,0,0.4)]">
            Cinematic Wedding Films
            <br />
            <span className="italic font-light">
              &amp; Photography in Essex
            </span>
          </h1>
          <p className="mt-6 text-white/80 text-base md:text-lg leading-relaxed font-light max-w-2xl">
            Award-winning wedding films and photography. Two videographers on
            every booking, full day coverage, and over 1000 weddings captured
            across Essex and the South East.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <button
              type="button"
              onClick={scrollToRetrieve}
              data-testid="hero-retrieve-btn"
              className="inline-flex items-center gap-2 bg-[#CCBA56] hover:bg-[#B5A244] text-[#1F1E1D] px-6 py-3 text-xs tracking-overline uppercase font-medium transition-colors duration-300"
            >
              Retrieve My Quote
              <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <a
              href="https://www.weddingvideo.co.uk/contact"
              target="_blank"
              rel="noreferrer"
              data-testid="hero-get-quote-btn"
              className="group inline-flex items-center gap-2 text-white border border-white/40 hover:border-white px-6 py-3 text-xs tracking-overline uppercase font-medium transition-all duration-300"
            >
              Get a Quote
              <ArrowRight
                className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                strokeWidth={1.5}
              />
            </a>
          </div>

          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/60 text-[10px] tracking-overline uppercase font-medium flex flex-col items-center gap-2">
            <div className="w-px h-12 bg-gradient-to-b from-transparent to-white/60" />
            Scroll
          </div>
        </div>
      </section>

      {/* Slim retrieve band */}
      <section
        ref={retrieveRef}
        className="px-6 md:px-12 lg:px-24 py-14 md:py-16 scroll-mt-8 fade-up"
      >
        <div className="flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-16">
          <div className="lg:max-w-md shrink-0">
            <div className="text-[#A9927D] text-xs tracking-overline uppercase font-medium mb-3">
              For our couples
            </div>
            <h2 className="font-display text-[#1F1E1D] text-3xl md:text-4xl leading-tight tracking-tight font-medium">
              Already got a quote?
            </h2>
            <p className="text-[#5C5A56] text-sm leading-relaxed font-light mt-3">
              Enter the email address your quote was sent to, and we'll email
              you a secure link to view it.
            </p>
          </div>
          <div className="flex-1 w-full">
            {sent ? (
              <div
                data-testid="retrieve-sent-confirmation"
                className="bg-white/80 border border-[#1F1E1D]/15 px-6 py-5 text-[#1F1E1D] max-w-xl"
              >
                <div className="text-sm font-medium mb-1">Check your inbox</div>
                <div className="text-sm text-[#5C5A56] leading-relaxed">
                  If we have a quote for {email}, a secure link is on its way.
                  Don't forget to check your junk folder!
                </div>
              </div>
            ) : (
              <form
                onSubmit={onRetrieve}
                className="flex flex-col sm:flex-row sm:items-end gap-6 max-w-xl"
                data-testid="retrieve-form"
              >
                <div className="relative flex-1">
                  <Mail
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8782]"
                    strokeWidth={1.5}
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    data-testid="retrieve-email-input"
                    className="w-full bg-transparent border-b border-[#1F1E1D]/30 focus:border-[#2F2E2E] text-[#1F1E1D] text-lg placeholder:text-[#8A8782] pl-7 pb-3 pt-2 outline-none transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="retrieve-submit-btn"
                  className="group inline-flex items-center justify-center gap-2 bg-[#2F2E2E] hover:bg-[#454341] text-white px-6 py-3 text-xs tracking-overline uppercase font-medium transition-colors duration-300 disabled:opacity-50 shrink-0"
                >
                  {loading ? "Sending…" : "Email my quote"}
                  <ArrowRight
                    className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                    strokeWidth={1.5}
                  />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* SEO content */}
      <SeoContent />

      {/* Footer */}
      <footer className="px-6 md:px-12 lg:px-24 py-8 text-[#8A8782] text-xs tracking-overline uppercase font-medium flex flex-wrap justify-between gap-4">
        <div>weddingvideo.co.uk</div>
        <div>07513 011 412</div>
        <div>alex@weddingvideo.co.uk</div>
      </footer>
    </div>
  );
}
